// Admin Order detail. Shows the proof image, the customer's designed mockup
// (Fabric.js render of design.spec on the garment), every line item, customer
// + shipping, the production-events timeline, and a status-update form that
// appends a new event.
import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { fabric } from 'fabric'
import { apiFetch, useApiSrc } from '../../lib/api'
import { bundleOrderZip, saveBlobAs, type BundleProductionFile } from '../../lib/bundle-order-zip'
import { StatusBadge } from './Overview'

interface OrderItem {
  id: string; qty: number; lengthCm: number | null; sheetIndex: number | null
  unitPriceCents: number; lineTotalCents: number
  product: { id: string; kind: string; name: string; slug: string } | null
  variant: { id: string; size: string | null; sku: string } | null
}
type Quality = 'excellent' | 'good' | 'acceptable' | 'low' | 'reject' | 'unknown'
interface ProductionFile {
  fileId: string; label: string; location: string | null; sheetIndex: number | null
  qty: number | null; printWidthMm: number; printHeightMm: number
  widthPx: number | null; heightPx: number | null; originalName: string | null
  mimeType: string | null; size: number
  dpiX: number | null; dpiY: number | null; dpiMin: number | null
  quality: Quality; downloadUrl: string; previewUrl: string
}
interface ProductionArtwork {
  kind: 'garment' | 'dtf'
  productSlug: string | null; colour: string | null; qty: Record<string, number> | null
  sheetWidthMm?: number | null; sheetHeightMm?: number | null
  totalFiles: number
  files: ProductionFile[]
  sheets: Array<{ index: number; label: string; files: ProductionFile[] }> | null
}
interface PrintLocation {
  id: string; name: string; left: number; top: number; width: number; height: number
  mockupUrl: string | null
}
interface GarmentMockupLocation {
  id: string; name: string
  canvas: object | null
  mockupUrl: string | null
  // Flat PNG baked at submit. Null on legacy submissions.
  thumbnailUrl: string | null
  printZone: { left: number; top: number; width: number; height: number }
  printWidthMm: number
}
interface GarmentMockup {
  kind: 'garment'
  productSlug: string | null
  colour: string | null
  qty: Record<string, number> | null
  mockupUrl: string | null
  printLocations: PrintLocation[]
  canvas: object | null
  placements: Array<{ location?: string; label?: string; printWidthMm?: number; printHeightMm?: number }> | null
  // Multi-side data — saved by the Designer when the customer puts artwork
  // on more than one print location. The OrderDetail renders one card per
  // entry below, with both an artwork-only-to-scale view and a garment
  // composite view.
  locations: GarmentMockupLocation[]
}
interface DtfMockupSheet {
  index: number; label: string; lengthCm: number | null
  canvas: object | null
  placements: Array<{ label?: string; printWidthMm?: number; printHeightMm?: number; qty?: number }> | null
  // Flat PNG baked at submit. Null on legacy submissions.
  thumbnailUrl: string | null
}
interface DtfMockup {
  kind: 'dtf'
  sheetWidthMm: number | null; sheetHeightMm: number | null
  marginCm: number | null; paddingCm: number | null
  sheets: DtfMockupSheet[]
}
type DesignMockup = GarmentMockup | DtfMockup

interface OrderDetail {
  order: {
    id: string; number: number; status: string; totalCents: number; subtotalCents: number; shippingCents: number
    currency: string; createdAt: string; paidAt: string | null; shippedAt: string | null
    guestEmail: string | null; paymentRef: string | null; notes: string | null
    shippingSnapshot: {
      fullName: string; email: string; phone?: string; line1: string; line2?: string
      city: string; region?: string; postcode: string; country: string
    }
  }
  customer: { id: string; email: string; firstName: string | null; lastName: string | null; role: string } | null
  items: OrderItem[]
  design: { id: string; kind: string; name: string | null } | null
  proofUrl: string | null
  productionArtwork: ProductionArtwork | null
  designMockup: DesignMockup | null
  events: Array<{ id: string; kind: string; note: string | null; createdAt: string }>
}

// 300 DPI is the studio's preferred minimum.
const QUALITY_STYLE: Record<Quality, { label: string; cls: string }> = {
  excellent:  { label: 'Excellent  300+ DPI',  cls: 'bg-emerald-100 text-emerald-800 border border-emerald-200' },
  good:       { label: 'Good  250-299 DPI',    cls: 'bg-emerald-50  text-emerald-700 border border-emerald-200' },
  acceptable: { label: 'Acceptable  200-249',  cls: 'bg-amber-50    text-amber-800   border border-amber-200' },
  low:        { label: 'Low  150-199 DPI',     cls: 'bg-orange-50   text-orange-800  border border-orange-200' },
  reject:     { label: 'Reject  <150 DPI',     cls: 'bg-red-50      text-red-800     border border-red-200' },
  unknown:    { label: 'Unknown',              cls: 'bg-surface     text-ink-3       border border-border' },
}
function formatBytes(n: number) {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

// `destructive: true` means the transition is hard to reverse and needs a
// confirmation dialog before firing. Cancel + manual refund both fall here;
// the rest are forward-flow status changes where mis-clicks are recoverable.
const STATUSES: Array<{ key: string; label: string; destructive?: boolean }> = [
  { key: 'pending_payment', label: 'Pending payment' },
  { key: 'paid',            label: 'Mark as Paid' },
  { key: 'in_production',   label: 'Move to In Production' },
  { key: 'shipped',         label: 'Mark as Shipped' },
  { key: 'delivered',       label: 'Mark as Delivered' },
  { key: 'cancelled',       label: 'Cancel order',          destructive: true },
  { key: 'refunded',        label: 'Mark as Refunded',      destructive: true },
]

export default function AdminOrderDetail() {
  const { id } = useParams<{ id: string }>()
  const { getAccessToken } = useAuth()
  const apiSrc = useApiSrc()
  const [data, setData] = useState<OrderDetail | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  // Proof background toggle. Transparent-PNG proofs need viewing on
  // white / grey / black so admins can QA against different garment
  // substrates.
  const [proofBg, setProofBg] = useState<'white' | 'grey' | 'black'>('white')
  // Client-side "Download originals" bundler state. Declared UP HERE
  // (above the early returns) so the hook order is identical on every
  // render — moving them below `if (!data) return …` triggers
  // "Rendered more hooks than during the previous render" and crashes
  // the whole page on first data arrival.
  const [bundling, setBundling] = useState(false)
  const [bundleStatus, setBundleStatus] = useState<string>('')

  async function load() {
    try {
      const d = await apiFetch<OrderDetail>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/orders/${id}`)
      setData(d); setErr(null)
    } catch (e) { setErr((e as Error).message) }
  }
  useEffect(() => { void load() /* eslint-disable-next-line */ }, [id])

  async function patch(status?: string, alsoNote?: string) {
    if (!data) return
    setBusy(true)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/orders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status, note: alsoNote || undefined }),
      })
      setNote('')
      await load()
    } catch (e) { alert(`Update failed: ${(e as Error).message}`) }
    finally { setBusy(false) }
  }

  async function refund() {
    if (!data) return
    if (!confirm(`Issue a full refund for $${(data.order.totalCents / 100).toFixed(2)}? This is final.`)) return
    setBusy(true)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/orders/${id}/refund`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'requested_by_customer' }),
      })
      await load()
    } catch (e) { alert(`Refund failed: ${(e as Error).message}`) }
    finally { setBusy(false) }
  }

  if (err) return <div className="p-8"><div className="card text-red-700">Error: {err}</div></div>
  if (!data) return <div className="p-8 text-ink-3 text-sm">Loading…</div>

  const { order, customer, items, design, proofUrl, productionArtwork, designMockup, events } = data
  const ship = order.shippingSnapshot

  // "Download originals" handler — runs entirely client-side so the per-
  // side / per-sheet PROOF PNGs included in the ZIP are baked from the
  // same Fabric.js pipeline the admin sees on screen (byte-identical to
  // the per-side "Download proof" PNG). State for this is declared up
  // top to keep hook order stable across renders.
  async function downloadOriginals() {
    if (bundling) return
    setBundling(true); setBundleStatus('Preparing…')
    try {
      const designKind: 'garment' | 'dtf' | string = designMockup?.kind ?? design?.kind ?? 'garment'
      const files: BundleProductionFile[] = (productionArtwork?.files ?? []).map((f) => ({
        originalName: f.originalName,
        url: f.downloadUrl,
        location: f.location,
        sheetIndex: f.sheetIndex,
        sheetLabel: productionArtwork?.sheets?.find((s) => s.index === f.sheetIndex)?.label ?? null,
      }))
      const blob = await bundleOrderZip({
        order: {
          number: order.number,
          status: order.status,
          createdAt: order.createdAt ?? null,
        },
        designKind,
        proofUrl: proofUrl ?? null,
        proofOriginalName: 'proof.png',
        garmentSides: designMockup?.kind === 'garment'
          ? designMockup.locations.map((l) => ({
              name: l.name,
              canvas: l.canvas,
              printZone: l.printZone,
              printWidthMm: l.printWidthMm,
            }))
          : undefined,
        dtfSheets: designMockup?.kind === 'dtf'
          ? designMockup.sheets.map((s) => ({
              label: s.label, index: s.index,
              canvas: s.canvas, lengthCm: s.lengthCm,
            }))
          : undefined,
        dtfProofBg: proofBg,
        files,
        apiSrc,
        onProgress: setBundleStatus,
      })
      saveBlobAs(blob, `order-${order.number}-files.zip`)
      setBundleStatus('Done')
    } catch (e) {
      alert('Bundle failed: ' + (e as Error).message)
    } finally {
      setBundling(false)
      setTimeout(() => setBundleStatus(''), 1500)
    }
  }

  return (
    <div className="p-8">
      <div className="mb-4">
        <Link to="/admin/orders" className="text-xs text-ink-3 hover:text-gold-dark">← All orders</Link>
      </div>
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow">Order detail</div>
          <h1 className="font-heading uppercase font-bold text-2xl text-ink font-mono normal-case">#{order.number}</h1>
          <div className="text-[10px] uppercase tracking-[0.14em] text-ink-3 font-mono mt-0.5">{order.id}</div>
          <div className="mt-1 flex items-center gap-3 text-xs text-ink-3">
            <StatusBadge status={order.status} />
            <span>Placed {new Date(order.createdAt).toLocaleString()}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.16em] text-ink-3">Total ({order.currency})</div>
          <div className="font-heading font-bold text-3xl text-ink">${(order.totalCents / 100).toFixed(2)}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">

        {/* Proof + items */}
        <div className="lg:col-span-2 space-y-6">
          {/* Proof — garment: render artwork-only per side at scale. DTF:
              render every sheet from its saved fabricJson, with a download
              button per sheet. Anything else: fall back to the legacy
              single-bitmap proof file. */}
          {designMockup && designMockup.kind === 'garment' && designMockup.locations.some((l) => l.canvas) ? (
            <ProofPerSideCard
              mockup={designMockup}
              proofBg={proofBg}
              setProofBg={setProofBg}
              onDownload={downloadOriginals}
              bundling={bundling}
              bundleStatus={bundleStatus}
              orderNumber={order.number} />
          ) : designMockup && designMockup.kind === 'dtf' && designMockup.sheets.some((s) => s.canvas) ? (
            <ProofDtfSheetsCard
              mockup={designMockup}
              proofBg={proofBg}
              setProofBg={setProofBg}
              onDownload={downloadOriginals}
              bundling={bundling}
              bundleStatus={bundleStatus}
              orderNumber={order.number} />
          ) : (
            <div className="card">
              <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                <div className="eyebrow !mb-0">Proof</div>
                {proofUrl && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-ink-3">
                      <span>Background</span>
                      <div className="inline-flex border border-border rounded overflow-hidden">
                        <button onClick={() => setProofBg('white')} aria-pressed={proofBg === 'white'}
                          className={'px-2.5 py-1 transition-colors ' + (proofBg === 'white' ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>White</button>
                        <button onClick={() => setProofBg('grey')} aria-pressed={proofBg === 'grey'}
                          className={'px-2.5 py-1 border-l border-border transition-colors ' + (proofBg === 'grey' ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>Grey</button>
                        <button onClick={() => setProofBg('black')} aria-pressed={proofBg === 'black'}
                          className={'px-2.5 py-1 border-l border-border transition-colors ' + (proofBg === 'black' ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>Black</button>
                      </div>
                    </div>
                    <a href={apiSrc(proofUrl)} download={`proof-${order.number}.png`}
                      className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-ink text-white hover:bg-ink-2 transition-colors whitespace-nowrap">
                      Download proof
                    </a>
                  </div>
                )}
              </div>
              {proofUrl ? (
                <a href={apiSrc(proofUrl)} target="_blank" rel="noreferrer" className="block border border-border rounded overflow-hidden"
                   style={{ background: proofBg === 'black' ? '#0C0D0F' : proofBg === 'grey' ? '#9CA3AF' : '#ffffff' }}>
                  <img src={apiSrc(proofUrl)} alt="Customer proof" className="w-full block max-h-[600px] object-contain" />
                </a>
              ) : (
                <p className="text-sm text-ink-3">No proof uploaded.</p>
              )}
              {design && (
                <div className="mt-3 text-xs text-ink-3">
                  Design <span className="font-mono text-ink-2">{design.id}</span> · kind <strong className="text-ink">{design.kind}</strong>
                </div>
              )}
            </div>
          )}

          {designMockup && <DesignMockupCard mockup={designMockup} apiSrc={apiSrc} />}

          {productionArtwork && (
            <div className="card">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div>
                  <div className="eyebrow">Production artwork</div>
                  <div className="text-sm text-ink-2">
                    {productionArtwork.kind === 'garment'
                      ? <>Garment - <strong className="text-ink uppercase">{productionArtwork.productSlug ?? 'garment'}</strong>{productionArtwork.colour ? <> - {productionArtwork.colour}</> : null} - {productionArtwork.totalFiles} file{productionArtwork.totalFiles === 1 ? '' : 's'} ready for press</>
                      : <>DTF gang sheet - {(productionArtwork.sheets?.length ?? 0)} sheet{(productionArtwork.sheets?.length ?? 0) === 1 ? '' : 's'}, {productionArtwork.totalFiles} transfer{productionArtwork.totalFiles === 1 ? '' : 's'} total{productionArtwork.sheetWidthMm && productionArtwork.sheetHeightMm ? <> - {productionArtwork.sheetWidthMm} x {productionArtwork.sheetHeightMm} mm per sheet</> : null}</>
                    }
                  </div>
                </div>
                <div className="text-xs text-ink-3"><strong className="text-ink">Preferred</strong> 300 DPI minimum</div>
              </div>

              {productionArtwork.qty && (
                <div className="mt-3 text-xs text-ink-3 flex flex-wrap gap-x-4 gap-y-1">
                  <span className="text-ink uppercase tracking-wider text-[10px] font-bold">Sizes</span>
                  {Object.entries(productionArtwork.qty).map(([size, q]) => (
                    <span key={size}><strong className="text-ink">{size}</strong> x {q}</span>
                  ))}
                </div>
              )}

              {productionArtwork.kind === 'garment' && (
                <div className="grid sm:grid-cols-2 gap-4 mt-5">
                  {productionArtwork.files.map((f) => <ArtworkTile key={f.fileId} f={f} apiSrc={apiSrc} />)}
                </div>
              )}

              {productionArtwork.kind === 'dtf' && productionArtwork.sheets && (
                <div className="mt-5 space-y-6">
                  {productionArtwork.sheets.map((s) => (
                    <div key={s.index}>
                      <div className="flex items-baseline justify-between border-b border-border pb-2 mb-3">
                        <h4 className="text-sm font-bold text-ink">{s.label}</h4>
                        <span className="text-xs text-ink-3">{s.files.reduce((n, f) => n + (f.qty ?? 1), 0)} transfer{s.files.length === 1 ? '' : 's'} - {s.files.length} unique file{s.files.length === 1 ? '' : 's'}</span>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {s.files.map((f) => <ArtworkTile key={f.fileId} f={f} apiSrc={apiSrc} />)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {productionArtwork.totalFiles > 1 && (
                <div className="mt-5 pt-4 border-t border-border text-xs text-ink-3 flex flex-wrap gap-x-5 gap-y-1">
                  <span><strong className="text-ink">{productionArtwork.files.filter(f => f.quality === 'excellent').length}</strong> excellent</span>
                  <span><strong className="text-ink">{productionArtwork.files.filter(f => f.quality === 'good').length}</strong> good</span>
                  <span><strong className="text-ink">{productionArtwork.files.filter(f => f.quality === 'acceptable').length}</strong> acceptable</span>
                  <span className="text-orange-700"><strong>{productionArtwork.files.filter(f => f.quality === 'low').length}</strong> low</span>
                  <span className="text-red-700"><strong>{productionArtwork.files.filter(f => f.quality === 'reject').length}</strong> reject</span>
                </div>
              )}
            </div>
          )}

          <div className="card !p-0 overflow-hidden">
            <div className="px-5 pt-5 eyebrow">Items</div>
            <table className="w-full text-sm mt-2">
              <thead className="bg-surface text-[10px] uppercase tracking-wider text-ink-3">
                <tr>
                  <th className="text-left px-5 py-2 font-bold">Item</th>
                  <th className="text-left px-5 py-2 font-bold">Detail</th>
                  <th className="text-right px-5 py-2 font-bold">Qty</th>
                  <th className="text-right px-5 py-2 font-bold">Unit</th>
                  <th className="text-right px-5 py-2 font-bold">Line</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((it) => (
                  <tr key={it.id}>
                    <td className="px-5 py-3 text-ink font-semibold">{it.product?.name ?? '—'}</td>
                    <td className="px-5 py-3 text-ink-2">
                      {it.variant?.size && <span className="uppercase font-semibold text-ink mr-2">{it.variant.size}</span>}
                      {it.variant?.sku && <span className="text-xs text-ink-3 mr-2">{it.variant.sku}</span>}
                      {it.lengthCm != null && <span className="font-semibold text-ink mr-2">{it.lengthCm} cm</span>}
                      {it.sheetIndex != null && <span className="text-xs text-ink-3">Sheet {it.sheetIndex + 1}</span>}
                    </td>
                    <td className="px-5 py-3 text-right text-ink-2">{it.qty}</td>
                    <td className="px-5 py-3 text-right text-ink-2">${(it.unitPriceCents / 100).toFixed(2)}</td>
                    <td className="px-5 py-3 text-right font-semibold text-ink">${(it.lineTotalCents / 100).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-surface">
                <tr className="text-xs text-ink-3">
                  <td className="px-5 py-2" colSpan={3}></td>
                  <td className="px-5 py-2 text-right">Subtotal</td>
                  <td className="px-5 py-2 text-right font-semibold text-ink">${(order.subtotalCents / 100).toFixed(2)}</td>
                </tr>
                <tr className="text-xs text-ink-3">
                  <td className="px-5 py-2" colSpan={3}></td>
                  <td className="px-5 py-2 text-right">Shipping</td>
                  <td className="px-5 py-2 text-right font-semibold text-ink">${(order.shippingCents / 100).toFixed(2)}</td>
                </tr>
                <tr className="text-sm">
                  <td className="px-5 py-3" colSpan={3}></td>
                  <td className="px-5 py-3 text-right font-bold text-ink">Total</td>
                  <td className="px-5 py-3 text-right font-bold text-ink">${(order.totalCents / 100).toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Production timeline */}
          <div className="card">
            <div className="eyebrow">Production timeline</div>
            {events.length === 0 ? (
              <p className="text-sm text-ink-3">No events yet.</p>
            ) : (
              <ol className="space-y-3 text-sm">
                {events.map((e) => (
                  <li key={e.id} className="flex items-start gap-3">
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-gold/15 text-gold-dark shrink-0">{e.kind.replace(/_/g, ' ')}</span>
                    <div className="flex-1">
                      {e.note && <div className="text-ink-2">{e.note}</div>}
                      <div className="text-[11px] text-ink-3">{new Date(e.createdAt).toLocaleString()}</div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {/* Sidebar — customer, shipping, status actions */}
        <div className="space-y-6">
          <div className="card">
            <div className="eyebrow">Customer</div>
            {customer ? (
              <div className="text-sm space-y-1">
                <div className="text-ink font-semibold">{[customer.firstName, customer.lastName].filter(Boolean).join(' ') || customer.email}</div>
                <div className="text-ink-2">{customer.email}</div>
                <div className="text-xs text-ink-3 uppercase tracking-wide">{customer.role}</div>
              </div>
            ) : (
              <div className="text-sm space-y-1">
                <div className="text-ink font-semibold">Guest</div>
                <div className="text-ink-2">{order.guestEmail}</div>
              </div>
            )}
          </div>

          <div className="card">
            <div className="eyebrow">Ship to</div>
            <div className="text-sm leading-relaxed text-ink-2">
              <div className="text-ink font-semibold">{ship.fullName}</div>
              <div>{ship.line1}{ship.line2 ? `, ${ship.line2}` : ''}</div>
              <div>{ship.city}{ship.region ? `, ${ship.region}` : ''} {ship.postcode}</div>
              <div>{ship.country}</div>
              {ship.phone && <div className="text-ink-3 mt-1">📞 {ship.phone}</div>}
            </div>
          </div>

          <div className="card">
            <div className="eyebrow">Update status</div>
            <div className="space-y-2">
              {STATUSES.filter((s) => s.key !== order.status).map((s) => (
                <button key={s.key} disabled={busy}
                  onClick={() => {
                    if (s.destructive && !confirm(`${s.label}? This is hard to reverse from the admin UI.`)) return
                    patch(s.key)
                  }}
                  className={'w-full text-left px-3 py-2 border rounded text-sm transition-colors disabled:opacity-50 ' +
                    (s.destructive
                      ? 'border-red-300 text-red-700 hover:border-red-500 hover:bg-red-50'
                      : 'border-border text-ink-2 hover:border-gold hover:bg-gold/5')}>
                  {s.label}
                </button>
              ))}
              {/* Refund — only meaningful for paid orders with a Stripe payment_ref. */}
              {order.paymentRef && order.status !== 'refunded' && (
                <button disabled={busy} onClick={refund}
                  className="w-full text-left px-3 py-2 border border-red-300 rounded text-sm text-red-700 hover:border-red-500 hover:bg-red-50 transition-colors disabled:opacity-50">
                  Refund via Stripe
                </button>
              )}
            </div>
            <div className="border-t border-border mt-4 pt-4">
              <div className="eyebrow">Add a note</div>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="input" placeholder="Optional context — visible in the timeline." />
              <button disabled={busy || !note.trim()} onClick={() => patch(undefined, note)}
                className="btn-outline mt-2 w-full justify-center text-xs">{busy ? 'Saving…' : 'Add note'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Designer canvas dimensions — must match Designer.tsx so a saved spec
// renders pixel-perfect over the garment mockup.
const CANVAS_W = 640
const CANVAS_H = 720

function DesignMockupCard({ mockup, apiSrc }: { mockup: DesignMockup; apiSrc: (path: string | null) => string }) {
  if (mockup.kind === 'garment') return <GarmentMockupCard mockup={mockup} apiSrc={apiSrc} />
  return <DtfMockupCard mockup={mockup} apiSrc={apiSrc} />
}

function GarmentMockupCard({ mockup, apiSrc }: { mockup: GarmentMockup; apiSrc: (path: string | null) => string }) {
  // Locations with real Fabric.js canvas data — drives both the per-side
  // artwork-only proof grid above + the per-side mockup composite grid here.
  const sides = mockup.locations.filter((l) => l.canvas)
  // Lightbox state: when set, the modal opens with the picked side rendered
  // full-page so admin can scrutinise the design in detail.
  const [lightboxSide, setLightboxSide] = useState<GarmentMockupLocation | null>(null)
  return (
    <div className="card">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="eyebrow">Customer mockup</div>
          <div className="text-sm text-ink-2">
            What the customer designed in the builder
            {mockup.productSlug && <> - <strong className="text-ink uppercase">{mockup.productSlug}</strong></>}
            {mockup.colour && <> - {mockup.colour}</>}
            {sides.length > 0 && <> - {sides.length} print location{sides.length === 1 ? '' : 's'} - click a tile to enlarge</>}
          </div>
        </div>
      </div>
      {sides.length > 0 ? (
        <>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
            {sides.map((s) => (
              <button key={s.id} onClick={() => setLightboxSide(s)}
                className="text-left group block focus:outline-none focus:ring-2 focus:ring-gold rounded">
                <div className="flex items-baseline justify-between mb-1.5">
                  <h4 className="text-sm font-bold text-ink group-hover:text-gold-dark">{s.name}</h4>
                  <span className="text-[10px] uppercase tracking-wider text-ink-3">~{s.printWidthMm} mm</span>
                </div>
                <div className="transition-transform group-hover:scale-[1.01]">
                  {/* Prefer the flat PNG baked at submit (no Fabric.js
                      runtime, identical to what the customer saw). Live
                      Fabric render is the fallback for legacy submissions. */}
                  {s.thumbnailUrl ? (
                    <img src={apiSrc(s.thumbnailUrl)} alt={`${s.name} mockup`}
                      className="w-full h-auto block border border-border rounded bg-surface" />
                  ) : (
                    <SingleSideMockupCanvas
                      mockupUrl={s.mockupUrl ?? mockup.mockupUrl}
                      canvas={s.canvas}
                      apiSrc={apiSrc}
                      mode="thumb"
                      printZone={s.printZone} />
                  )}
                </div>
              </button>
            ))}
          </div>
          <MockupLightbox
            open={!!lightboxSide}
            title={lightboxSide?.name ?? ''}
            subtitle={lightboxSide ? `~${lightboxSide.printWidthMm} mm wide` : ''}
            onClose={() => setLightboxSide(null)}>
            {lightboxSide && (
              <div className="mx-auto" style={{ maxWidth: 720 }}>
                {lightboxSide.thumbnailUrl ? (
                  <img src={apiSrc(lightboxSide.thumbnailUrl)} alt={`${lightboxSide.name} mockup`}
                    className="w-full h-auto block border border-border rounded bg-surface" />
                ) : (
                  <SingleSideMockupCanvas
                    mockupUrl={lightboxSide.mockupUrl ?? mockup.mockupUrl}
                    canvas={lightboxSide.canvas}
                    apiSrc={apiSrc}
                    mode="full" />
                )}
              </div>
            )}
          </MockupLightbox>
        </>
      ) : (
        // Legacy single-canvas / demo-data render — kept for orders saved
        // before the multi-location spec landed.
        <DesignMockupCanvas mockup={mockup} apiSrc={apiSrc} />
      )}
    </div>
  )
}

// One side's design composited on top of its garment mockup. Used by the
// multi-side grid above. Renders the WHOLE 640x720 composite — no zoom or
// print-zone clipping; thumbnails and the lightbox both show the full
// garment with the artwork in context. `mode` and `printZone` are kept in
// the signature so existing call sites continue to typecheck; they're not
// used in the layout anymore.
function SingleSideMockupCanvas({ mockupUrl, canvas, apiSrc }: {
  mockupUrl: string | null
  canvas: object | null
  apiSrc: (path: string | null) => string
  mode?: 'thumb' | 'full'
  printZone?: { left: number; top: number; width: number; height: number }
}) {
  const ref = useRef<HTMLCanvasElement | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || !canvas) return
    const c = new fabric.StaticCanvas(el, {
      width: CANVAS_W, height: CANVAS_H,
      backgroundColor: 'transparent', selection: false, renderOnAddRemove: false,
    })
    try { c.loadFromJSON(canvas, () => { c.requestRenderAll() }) }
    catch (e) { console.warn('side canvas load failed:', (e as Error).message) }
    return () => { c.dispose() }
  }, [canvas])

  return (
    <div className="relative mx-auto bg-surface border border-border rounded overflow-hidden"
         style={{ width: '100%', maxWidth: CANVAS_W, aspectRatio: `${CANVAS_W} / ${CANVAS_H}` }}>
      {mockupUrl && (
        <img src={apiSrc(mockupUrl)} alt="garment mockup"
             className="absolute inset-0 w-full h-full object-contain pointer-events-none" draggable={false} />
      )}
      {canvas && (
        <canvas ref={ref} className="absolute inset-0 w-full h-full pointer-events-none"
                width={CANVAS_W} height={CANVAS_H} />
      )}
    </div>
  )
}

// Artwork-only-at-scale rendering. Strips the garment background entirely
// and clips the Fabric canvas to the print-zone rectangle by translating
// the viewport so the print zone's top-left lands at (0,0) of a static
// canvas sized to the print-zone dimensions. The wrapper then sizes the
// rendered output proportional to the real-world print width (mm), giving
// production a faithful "this is what the print looks like, at this size"
// preview.
function ArtworkOnlyCanvas({ side, maxWidthPx = 320 }: { side: GarmentMockupLocation; maxWidthPx?: number }) {
  const ref = useRef<HTMLCanvasElement | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || !side.canvas) return
    const z = side.printZone
    const c = new fabric.StaticCanvas(el, {
      width: z.width, height: z.height,
      backgroundColor: '#ffffff', selection: false, renderOnAddRemove: false,
    })
    try {
      c.loadFromJSON(side.canvas, () => {
        // Translate the viewport so the print zone aligns with the canvas
        // origin. Everything outside the print zone clips automatically.
        c.setViewportTransform([1, 0, 0, 1, -z.left, -z.top])
        c.requestRenderAll()
      })
    } catch (e) { console.warn('artwork-only load failed:', (e as Error).message) }
    return () => { c.dispose() }
  }, [side])
  const aspect = side.printZone.height / side.printZone.width || 1
  // Convert the print-area mm into a screen width. Cap so very wide prints
  // don't blow out the layout; admin can click through for full size.
  const widthPx = Math.min(maxWidthPx, side.printWidthMm * 2.2)
  const heightPx = widthPx * aspect
  return (
    <div className="border border-border rounded bg-white p-2 mx-auto"
         style={{ width: widthPx + 16 }}>
      <div className="relative" style={{ width: widthPx, height: heightPx }}>
        <canvas ref={ref} width={side.printZone.width} height={side.printZone.height}
                className="absolute inset-0 w-full h-full" />
      </div>
      <div className="text-center text-[10px] uppercase tracking-wider text-ink-3 mt-1.5 font-mono">
        ~{side.printWidthMm} mm wide
      </div>
    </div>
  )
}

// Per-side artwork-only proof grid — replaces the old single-bitmap Proof
// section. Renders one GarmentSideProofCanvas per location with media so
// admin sees the actual artwork for each printed side at the print zone's
// real-world dimensions AND can download a print-ready PNG / PDF per side.
function ProofPerSideCard({ mockup, proofBg, setProofBg, onDownload, bundling, bundleStatus, orderNumber }: {
  mockup: GarmentMockup
  proofBg: 'white' | 'grey' | 'black'
  setProofBg: (b: 'white' | 'grey' | 'black') => void
  onDownload: () => void
  bundling: boolean
  bundleStatus: string
  orderNumber: number
}) {
  const sides = mockup.locations.filter((l) => l.canvas)
  const [activeIdx, setActiveIdx] = useState(0)
  const active = sides[Math.min(activeIdx, sides.length - 1)]
  return (
    <div className="card">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="eyebrow !mb-0">Proof - print-ready, to scale</div>
          <div className="text-sm text-ink-2">One tab per printed side. Each side renders at the print zone's real-world dimensions on a transparent background; download as PNG (300 DPI) or PDF (mm-accurate).</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-ink-3" title="Preview backdrop only — exported PNGs stay fully transparent">
            <span>Preview on</span>
            <div className="inline-flex border border-border rounded overflow-hidden">
              <button onClick={() => setProofBg('white')} aria-pressed={proofBg === 'white'}
                className={'px-2.5 py-1 transition-colors ' + (proofBg === 'white' ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>White</button>
              <button onClick={() => setProofBg('grey')} aria-pressed={proofBg === 'grey'}
                className={'px-2.5 py-1 border-l border-border transition-colors ' + (proofBg === 'grey' ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>Grey</button>
              <button onClick={() => setProofBg('black')} aria-pressed={proofBg === 'black'}
                className={'px-2.5 py-1 border-l border-border transition-colors ' + (proofBg === 'black' ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>Black</button>
            </div>
          </div>
          <button onClick={onDownload} disabled={bundling}
            title="Bundle every original artwork file from this order plus a print-ready proof PNG per printed side into a single .zip"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-ink text-white hover:bg-ink-2 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-wait">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {bundling ? (bundleStatus || 'Bundling…') : 'Download originals'}
          </button>
        </div>
      </div>

      {sides.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-3 border-b border-border pb-1">
          {sides.map((s, i) => (
            <button key={s.id} onClick={() => setActiveIdx(i)}
              className={'px-3 py-1.5 text-xs font-semibold uppercase tracking-wide rounded transition-colors ' +
                (i === activeIdx ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>
              {s.name}
            </button>
          ))}
        </div>
      )}

      {/* No outer panel — the proof box IS the print area, with the
          per-side checker / dark backdrop revealing the transparent
          edges. Wrapping it in another padded box added "extra space"
          around the print area, which is what we explicitly don't want. */}
      {active ? (
        <GarmentSideProofCanvas side={active} proofBg={proofBg} orderNumber={orderNumber} />
      ) : (
        <p className="text-sm text-center py-6 text-ink-3">
          No per-side canvas data on this design. (Saved before the multi-location spec; see the legacy proof file linked above.)
        </p>
      )}
    </div>
  )
}

// Garment-side proof: render ONCE at 300 DPI into an offscreen Fabric
// canvas, then use the resulting PNG dataURL as BOTH the on-screen preview
// (scaled down by the browser via <img>) AND the source of the downloaded
// PNG/PDF. Single render path = zero possibility of preview/download drift.
// Kept byte-identical to MockupCanvases.tsx's GarmentSideProof.
function GarmentSideProofCanvas({ side, proofBg, orderNumber }: {
  side: GarmentMockupLocation
  proofBg: 'white' | 'grey' | 'black'
  orderNumber: number
}) {
  const [proofDataUrl, setProofDataUrl] = useState<string | null>(null)
  const [proofPx, setProofPx] = useState<{ w: number; h: number } | null>(null)
  const [rendering, setRendering] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const printWidthMm  = Math.max(1, side.printWidthMm)
  const printHeightMm = printWidthMm * (side.printZone.height / Math.max(1, side.printZone.width))

  useEffect(() => {
    if (!side.canvas) { setProofDataUrl(null); setProofPx(null); return }
    let cancelled = false
    setRendering(true)
    ;(async () => {
      try {
        // Idiomatic Fabric.js export: load the customer's saved JSON into a
        // single StaticCanvas at the Designer's native size, then emit just
        // the print-zone sub-rect via toDataURL({left,top,width,height,
        // multiplier}). Fabric handles the crop + scale in one call — no
        // viewport-transform math for us to maintain.
        const tmpEl = document.createElement('canvas')
        tmpEl.width = CANVAS_W
        tmpEl.height = CANVAS_H
        const c = new fabric.StaticCanvas(tmpEl, {
          width: CANVAS_W, height: CANVAS_H,
          backgroundColor: '',
          enableRetinaScaling: false, renderOnAddRemove: false,
        })
        try {
          await new Promise<void>((resolve) => {
            c.loadFromJSON(side.canvas, () => {
              c.renderAll()
              resolve()
            })
          })
          const PX_PER_MM_AT_300_DPI = 300 / 25.4
          const multiplier = (printWidthMm * PX_PER_MM_AT_300_DPI) / Math.max(1, side.printZone.width)
          const url = c.toDataURL({
            format: 'png',
            left:   side.printZone.left,
            top:    side.printZone.top,
            width:  side.printZone.width,
            height: side.printZone.height,
            multiplier,
          })
          const w = Math.round(side.printZone.width  * multiplier)
          const h = Math.round(side.printZone.height * multiplier)
          if (!cancelled) { setProofDataUrl(url); setProofPx({ w, h }) }
        } finally {
          c.dispose()
        }
      } catch (e) {
        if (!cancelled) console.warn('garment proof render failed:', (e as Error).message)
      } finally {
        if (!cancelled) setRendering(false)
      }
    })()
    return () => { cancelled = true }
  }, [side, printWidthMm])

  useEffect(() => {
    if (!menuOpen) return
    function onDown(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [menuOpen])

  const slug = side.name.toLowerCase().replace(/\s+/g, '-')

  function downloadPng() {
    if (!proofDataUrl) return
    const a = document.createElement('a')
    a.href = proofDataUrl
    a.download = `proof-${orderNumber}-${slug}.png`
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    setMenuOpen(false)
  }

  async function downloadPdf() {
    if (!proofDataUrl) return
    setExporting(true)
    try {
      const { jsPDF } = await import('jspdf')
      const pdf = new jsPDF({
        orientation: printWidthMm > printHeightMm ? 'landscape' : 'portrait',
        unit: 'mm', format: [printWidthMm, printHeightMm], compress: true,
      })
      pdf.addImage(proofDataUrl, 'PNG', 0, 0, printWidthMm, printHeightMm)
      pdf.save(`proof-${orderNumber}-${slug}.pdf`)
      setMenuOpen(false)
    } catch (e) { alert('PDF export failed: ' + (e as Error).message) }
    finally { setExporting(false) }
  }

  const DISPLAY_MAX_W = 480
  const aspect = side.printZone.height / Math.max(1, side.printZone.width)
  const widthPx = Math.min(DISPLAY_MAX_W, printWidthMm * 2.2)
  const heightPx = widthPx * aspect

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="text-left">
          <div className={'text-xs font-bold uppercase tracking-wider ' + (proofBg === 'black' ? 'text-white' : 'text-ink')}>
            {side.name}
          </div>
          <div className={'text-[11px] font-mono mt-0.5 ' + (proofBg === 'black' ? 'text-white/60' : 'text-ink-3')}>
            {printWidthMm.toFixed(0)} x {printHeightMm.toFixed(0)} mm · transparent{rendering ? ' · rendering…' : ''}
          </div>
        </div>
        <div ref={menuRef} className="relative">
          <button onClick={() => setMenuOpen((o) => !o)} disabled={exporting || !proofDataUrl}
            aria-haspopup="menu" aria-expanded={menuOpen}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded bg-ink text-white hover:bg-ink-2 transition-colors whitespace-nowrap disabled:opacity-60">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {exporting ? 'Exporting…' : 'Download proof'}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" className="opacity-70">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          {menuOpen && (
            <div role="menu"
              className="absolute right-0 top-[calc(100%+4px)] z-30 bg-white border border-border rounded shadow-lg overflow-hidden min-w-[170px]"
              onMouseDown={(e) => e.stopPropagation()}>
              <button role="menuitem" onClick={downloadPng} disabled={!proofDataUrl}
                className="flex items-center justify-between w-full text-left px-3 py-2 text-xs hover:bg-surface gap-3 disabled:opacity-60">
                <span><strong className="text-ink">PNG</strong> 300 DPI</span>
                <span className="text-[10px] uppercase tracking-wider text-ink-3 font-mono">
                  {proofPx ? `${proofPx.w}x${proofPx.h}px` : '…'}
                </span>
              </button>
              <button role="menuitem" onClick={downloadPdf} disabled={exporting || !proofDataUrl}
                className="flex items-center justify-between w-full text-left px-3 py-2 text-xs hover:bg-surface gap-3 border-t border-border disabled:opacity-60">
                <span><strong className="text-ink">PDF</strong> press-ready</span>
                <span className="text-[10px] uppercase tracking-wider text-ink-3 font-mono">
                  {printWidthMm.toFixed(0)}x{printHeightMm.toFixed(0)}mm
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="mx-auto" style={{ width: widthPx }}>
        {/* The preview displays the EXACT SAME PNG that gets downloaded,
            scaled down by the browser via <img>. Single render path = no
            positional drift between preview and download. Backdrop is
            preview-only; the PNG itself stays fully transparent. */}
        <div className="relative overflow-hidden"
             style={{
               width: widthPx,
               height: heightPx,
               boxShadow: '0 0 0 1px var(--border, rgba(0,0,0,0.1))',
               background: proofBg === 'black'
                 ? '#0C0D0F'
                 : proofBg === 'grey'
                   ? '#9CA3AF'
                   : 'conic-gradient(at 50% 50%, #ECECEC 25%, #FAFAFA 0 50%, #ECECEC 0 75%, #FAFAFA 0)',
               backgroundSize: proofBg === 'white' ? '16px 16px' : undefined,
             }}>
          {proofDataUrl ? (
            <img src={proofDataUrl} alt={`${side.name} proof`}
                 className="absolute inset-0 w-full h-full block"
                 style={{ objectFit: 'fill' }} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-ink-3">
              {rendering ? 'Rendering proof…' : 'No canvas data'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function DesignMockupCanvas({ mockup, apiSrc }: { mockup: GarmentMockup; apiSrc: (path: string | null) => string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  // Boot a read-only Fabric.js static canvas with the saved spec. Designer
  // saved at 640x720 so we render at the same size and just style it
  // responsively. Skips entirely when spec.canvas is absent (demo orders).
  useEffect(() => {
    const el = canvasRef.current
    if (!el || !mockup.canvas) return
    const c = new fabric.StaticCanvas(el, {
      width: CANVAS_W, height: CANVAS_H,
      backgroundColor: 'transparent', selection: false, renderOnAddRemove: false,
    })
    try {
      c.loadFromJSON(mockup.canvas, () => { c.requestRenderAll() })
    } catch (e) { console.warn('mockup canvas load failed:', (e as Error).message) }
    return () => { c.dispose() }
  }, [mockup.canvas])

  return (
    <div className="relative mx-auto bg-surface border border-border rounded overflow-hidden"
         style={{ width: '100%', maxWidth: CANVAS_W, aspectRatio: `${CANVAS_W} / ${CANVAS_H}` }}>
      {/* Garment mockup behind everything */}
      {mockup.mockupUrl ? (
        <img src={apiSrc(mockup.mockupUrl)} alt="garment mockup"
             className="absolute inset-0 w-full h-full object-contain pointer-events-none"
             draggable={false} />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-ink-3">No mockup image configured for this garment</div>
      )}

      {/* Real customer design — Fabric.js canvas overlay */}
      {mockup.canvas && (
        <canvas ref={canvasRef}
                className="absolute inset-0 w-full h-full pointer-events-none"
                width={CANVAS_W} height={CANVAS_H} />
      )}

      {/* Demo/seeded data without canvas — draw placement zones as labelled
          rectangles using the product's print_locations geometry. */}
      {!mockup.canvas && mockup.placements && mockup.placements.map((p, i) => {
        const loc = mockup.printLocations.find((l) =>
          l.id === p.location || l.name.toLowerCase() === (p.label ?? '').toLowerCase() ||
          l.name.toLowerCase().replace(/\s+/g, '_') === (p.location ?? '').toLowerCase())
        if (!loc) return null
        return (
          <div key={i}
            className="absolute border-2 border-gold bg-gold/10 flex items-start justify-center"
            style={{
              left: `${(loc.left / CANVAS_W) * 100}%`,
              top: `${(loc.top / CANVAS_H) * 100}%`,
              width: `${(loc.width / CANVAS_W) * 100}%`,
              height: `${(loc.height / CANVAS_H) * 100}%`,
            }}>
            <span className="bg-gold text-ink px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold mt-1 rounded-sm">
              {p.label ?? loc.name}{p.printWidthMm ? ` - ${p.printWidthMm}x${p.printHeightMm}mm` : ''}
            </span>
          </div>
        )
      })}
    </div>
  )
}

function DtfMockupCard({ mockup, apiSrc }: { mockup: DtfMockup; apiSrc: (path: string | null) => string }) {
  const [lightboxSheet, setLightboxSheet] = useState<DtfMockupSheet | null>(null)
  return (
    <div className="card">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="eyebrow">Customer mockup</div>
          <div className="text-sm text-ink-2">
            DTF gang sheet{mockup.sheets.length > 1 ? `s (${mockup.sheets.length})` : ''}
            {mockup.sheetWidthMm && mockup.sheetHeightMm && <> - {mockup.sheetWidthMm}x{mockup.sheetHeightMm} mm</>}
            {' - click a sheet to enlarge'}
          </div>
        </div>
      </div>
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
        {mockup.sheets.map((s) => (
          <button key={s.index} onClick={() => setLightboxSheet(s)}
            className="text-left group block focus:outline-none focus:ring-2 focus:ring-gold rounded">
            <div className="transition-transform group-hover:scale-[1.01]">
              {/* Prefer the flat PNG baked at submit (no Fabric.js); live
                  Fabric render is the fallback for legacy submissions. */}
              {s.thumbnailUrl ? (
                <div>
                  <div className="flex items-baseline justify-between mb-2 gap-2">
                    <h4 className="text-sm font-bold text-ink truncate">{s.label}</h4>
                    {s.lengthCm && <span className="text-[10px] uppercase tracking-wider text-ink-3 shrink-0">57 x {s.lengthCm} cm</span>}
                  </div>
                  <div className="bg-surface border border-border rounded flex items-center justify-center p-2" style={{ aspectRatio: '4 / 5' }}>
                    <img src={apiSrc(s.thumbnailUrl)} alt={`${s.label} mockup`} className="max-w-full max-h-full block" />
                  </div>
                </div>
              ) : (
                <DtfSheetCanvas sheet={s} apiSrc={apiSrc} mode="thumb" />
              )}
            </div>
          </button>
        ))}
      </div>
      <MockupLightbox
        open={!!lightboxSheet}
        title={lightboxSheet?.label ?? ''}
        subtitle={lightboxSheet?.lengthCm ? `60 x ${lightboxSheet.lengthCm} cm` : ''}
        onClose={() => setLightboxSheet(null)}>
        {lightboxSheet && (
          <div className="mx-auto" style={{ maxWidth: 760 }}>
            {lightboxSheet.thumbnailUrl ? (
              <img src={apiSrc(lightboxSheet.thumbnailUrl)} alt={`${lightboxSheet.label} mockup`}
                className="w-full h-auto block border border-border rounded bg-surface" />
            ) : (
              <DtfSheetCanvas sheet={lightboxSheet} apiSrc={apiSrc} mode="full" />
            )}
          </div>
        )}
      </MockupLightbox>
    </div>
  )
}

// Shared full-page lightbox modal. Click backdrop or Esc / × to close.
function MockupLightbox({ open, title, subtitle, onClose, children }: {
  open: boolean
  title: string
  subtitle?: string
  onClose: () => void
  children: React.ReactNode
}) {
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    // Lock body scroll while the lightbox is open so the page beneath
    // doesn't drift around.
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto"
         onClick={onClose}>
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-3 border-b border-border flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3">Customer mockup</div>
            <h3 className="text-ink font-heading uppercase font-bold text-lg truncate">{title}</h3>
            {subtitle && <div className="text-xs text-ink-3">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="text-ink-3 hover:text-ink text-2xl leading-none">×</button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          {children}
        </div>
      </div>
    </div>
  )
}

function DtfSheetCanvas({ sheet, apiSrc, mode = 'full' }: {
  sheet: DtfMockupSheet
  apiSrc: (path: string | null) => string
  mode?: 'thumb' | 'full'
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  // DTF sheet canvas dimensions: width is the fixed sheet width (57 cm at
  // 10 px/cm = 600 px), length varies. Designer saves fabric JSON with that
  // coordinate space.
  const SHEET_W = 570
  const SHEET_H = (sheet.lengthCm ?? 100) * 10

  useEffect(() => {
    const el = canvasRef.current
    if (!el || !sheet.canvas) return
    const c = new fabric.StaticCanvas(el, {
      width: SHEET_W, height: SHEET_H,
      backgroundColor: '#ffffff', selection: false, renderOnAddRemove: false,
    })
    try {
      c.loadFromJSON(sheet.canvas, () => { c.requestRenderAll() })
    } catch (e) { console.warn('dtf sheet load failed:', (e as Error).message) }
    return () => { c.dispose() }
  }, [sheet.canvas, SHEET_H])

  // The sheet itself — preserves the true 60:length aspect ratio so the
  // proportions are honest. Shared between thumb + full mode; the parent
  // wrapper decides the visible footprint.
  const sheetBox = (
    <div className="relative bg-white border border-border rounded overflow-hidden"
         style={{ aspectRatio: `${SHEET_W} / ${SHEET_H}`, width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100%' }}>
      {sheet.canvas ? (
        <canvas ref={canvasRef} width={SHEET_W} height={SHEET_H} className="w-full h-full block" />
      ) : sheet.placements ? (
        <div className="absolute inset-2 grid gap-1.5"
             style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gridAutoRows: 'minmax(80px, auto)' }}>
          {sheet.placements.map((p, i) => (
            <div key={i} className="border-2 border-dashed border-ink-3 bg-surface flex items-center justify-center text-center p-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-ink-2">
                {p.label}{p.qty && p.qty > 1 ? ` x ${p.qty}` : ''}
                {p.printWidthMm && p.printHeightMm && <div className="text-ink-3 mt-1">{p.printWidthMm}x{p.printHeightMm}mm</div>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-ink-3">Empty sheet</div>
      )}
      {void apiSrc /* reserved for per-placement file thumbnails */}
    </div>
  )

  // Thumbnail mode — letterbox the WHOLE sheet into a fixed-aspect tile
  // (4:5) on a neutral background so a 200cm sheet doesn't blow out the
  // grid cell and a 50cm sheet doesn't look squashed. Sheet stays centered
  // and the full layout is visible at a glance.
  if (mode === 'thumb') {
    return (
      <div>
        <div className="flex items-baseline justify-between mb-2 gap-2">
          <h4 className="text-sm font-bold text-ink truncate">{sheet.label}</h4>
          {sheet.lengthCm && <span className="text-[10px] uppercase tracking-wider text-ink-3 shrink-0">57 x {sheet.lengthCm} cm</span>}
        </div>
        <div className="bg-surface border border-border rounded flex items-center justify-center p-2"
             style={{ aspectRatio: '4 / 5' }}>
          {sheetBox}
        </div>
      </div>
    )
  }

  // Full mode (lightbox) — render the sheet at its natural max width.
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <h4 className="text-sm font-bold text-ink">{sheet.label}</h4>
        {sheet.lengthCm && <span className="text-xs text-ink-3">57 x {sheet.lengthCm} cm</span>}
      </div>
      <div className="mx-auto" style={{ maxWidth: SHEET_W }}>
        {sheetBox}
      </div>
    </div>
  )
}

// One DTF sheet rendered from its saved Fabric.js JSON, with a click-to-
// download split button (PNG or PDF). Used by ProofDtfSheetsCard below.
function DtfSheetProofCanvas({ sheet, proofBg, orderNumber }: {
  sheet: DtfMockupSheet
  proofBg: 'white' | 'grey' | 'black'
  orderNumber: number
}) {
  const ref = useRef<HTMLCanvasElement | null>(null)
  // Sheet is fixed 57 cm wide × admin-set length; length default 100 cm.
  // 10 px/cm matches the customer DTF builder so the spec coordinates land
  // pixel-perfect.
  const SHEET_W = 570
  const SHEET_H = (sheet.lengthCm ?? 100) * 10
  const [menuOpen, setMenuOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el || !sheet.canvas) return
    // Transparent canvas — DTF transfers print on clear film so the
    // exported PNG must carry alpha (white-ink underlay is driven by
    // the alpha channel). Substrate colour shown in the preview is a
    // CSS backdrop, not baked into the file.
    const c = new fabric.StaticCanvas(el, {
      width: SHEET_W, height: SHEET_H,
      backgroundColor: '',
      selection: false, renderOnAddRemove: false,
    })
    try {
      c.loadFromJSON(sheet.canvas, () => {
        // loadFromJSON restores backgroundColor from the spec, so older
        // saves with `#ffffff` baked in would still bleed through —
        // force transparent again after the load.
        c.setBackgroundColor('', () => {})
        c.requestRenderAll()
      })
    }
    catch (e) { console.warn('dtf proof sheet load failed:', (e as Error).message) }
    return () => { c.dispose() }
  }, [sheet.canvas, SHEET_H])

  // Close the menu when the admin clicks outside.
  useEffect(() => {
    if (!menuOpen) return
    function onDown(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [menuOpen])

  const slug = sheet.label.toLowerCase().replace(/\s+/g, '-')

  function downloadPng() {
    const el = ref.current; if (!el) return
    try {
      const url = el.toDataURL('image/png')
      const a = document.createElement('a')
      a.href = url
      a.download = `proof-${orderNumber}-${slug}.png`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      setMenuOpen(false)
    } catch (e) { alert('Download failed: ' + (e as Error).message) }
  }

  async function downloadPdf() {
    const el = ref.current; if (!el) return
    setExporting(true)
    try {
      // Wrap the canvas PNG in a single-page PDF sized to the sheet's actual
      // print dimensions (57 cm × admin-set length). Production can hand the
      // PDF straight to press without manual scaling.
      const url = el.toDataURL('image/png')
      const { jsPDF } = await import('jspdf')
      const widthCm  = 57
      const heightCm = sheet.lengthCm ?? 100
      const pdf = new jsPDF({
        orientation: widthCm > heightCm ? 'landscape' : 'portrait',
        unit: 'cm',
        format: [widthCm, heightCm],
        compress: true,
      })
      pdf.addImage(url, 'PNG', 0, 0, widthCm, heightCm)
      pdf.save(`proof-${orderNumber}-${slug}.pdf`)
      setMenuOpen(false)
    } catch (e) { alert('PDF export failed: ' + (e as Error).message) }
    finally { setExporting(false) }
  }

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <h4 className="text-sm font-bold text-ink">{sheet.label}</h4>
        <div className="flex items-center gap-3">
          {sheet.lengthCm && <span className="text-xs text-ink-3">57 x {sheet.lengthCm} cm</span>}
          <div ref={menuRef} className="relative">
            <button onClick={() => setMenuOpen((o) => !o)} disabled={exporting}
              aria-haspopup="menu" aria-expanded={menuOpen}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded bg-ink text-white hover:bg-ink-2 transition-colors whitespace-nowrap disabled:opacity-60">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              {exporting ? 'Exporting…' : 'Download sheet'}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" className="opacity-70">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            {menuOpen && (
              <div role="menu"
                className="absolute right-0 top-[calc(100%+4px)] z-30 bg-white border border-border rounded shadow-lg overflow-hidden min-w-[160px]"
                onMouseDown={(e) => e.stopPropagation()}>
                <button role="menuitem" onClick={downloadPng}
                  className="flex items-center justify-between w-full text-left px-3 py-2 text-xs hover:bg-surface gap-3">
                  <span><strong className="text-ink">PNG</strong> bitmap</span>
                  <span className="text-[10px] uppercase tracking-wider text-ink-3">{SHEET_W}x{SHEET_H}px</span>
                </button>
                <button role="menuitem" onClick={downloadPdf} disabled={exporting}
                  className="flex items-center justify-between w-full text-left px-3 py-2 text-xs hover:bg-surface gap-3 border-t border-border disabled:opacity-60">
                  <span><strong className="text-ink">PDF</strong> press-ready</span>
                  <span className="text-[10px] uppercase tracking-wider text-ink-3">57x{sheet.lengthCm ?? 100}cm</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="relative mx-auto border border-border rounded overflow-hidden"
           style={{
             width: '100%', maxWidth: SHEET_W, aspectRatio: `${SHEET_W} / ${SHEET_H}`,
             background: proofBg === 'black'
               ? '#0C0D0F'
               : proofBg === 'grey'
                 ? '#9CA3AF'
                 // White mode shows a subtle checker so the admin can SEE
                 // that the sheet's transparent regions are actually
                 // transparent — the exported PNG always carries full
                 // alpha regardless of this backdrop.
                 : 'conic-gradient(at 50% 50%, #ECECEC 25%, #FAFAFA 0 50%, #ECECEC 0 75%, #FAFAFA 0)',
             backgroundSize: proofBg === 'white' ? '16px 16px' : undefined,
           }}>
        <canvas ref={ref} width={SHEET_W} height={SHEET_H} className="w-full h-full block" />
      </div>
    </div>
  )
}

// Per-sheet DTF proof grid — replaces the old single-bitmap Proof card for
// DTF orders so production sees every gang-sheet, not just whichever was
// active at submit time. Each sheet renders its own Fabric.js canvas with a
// per-sheet download button alongside the legacy proof file download.
function ProofDtfSheetsCard({ mockup, proofBg, setProofBg, onDownload, bundling, bundleStatus, orderNumber }: {
  mockup: DtfMockup
  proofBg: 'white' | 'grey' | 'black'
  setProofBg: (b: 'white' | 'grey' | 'black') => void
  onDownload: () => void
  bundling: boolean
  bundleStatus: string
  orderNumber: number
}) {
  const sheets = mockup.sheets.filter((s) => s.canvas)
  const [activeIdx, setActiveIdx] = useState(0)
  const active = sheets[Math.min(activeIdx, sheets.length - 1)]
  return (
    <div className="card">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="eyebrow !mb-0">Proof - DTF gang sheets</div>
          <div className="text-sm text-ink-2">
            {sheets.length} sheet{sheets.length === 1 ? '' : 's'} ready - exports as transparent PNG so the printer can drive its white-ink underlay
            {mockup.sheetWidthMm && mockup.sheetHeightMm && <> - {mockup.sheetWidthMm} x {mockup.sheetHeightMm} mm</>}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-ink-3" title="Preview backdrop only — exported PNGs stay fully transparent">
            <span>Preview on</span>
            <div className="inline-flex border border-border rounded overflow-hidden">
              <button onClick={() => setProofBg('white')} aria-pressed={proofBg === 'white'}
                className={'px-2.5 py-1 transition-colors ' + (proofBg === 'white' ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>White</button>
              <button onClick={() => setProofBg('grey')} aria-pressed={proofBg === 'grey'}
                className={'px-2.5 py-1 border-l border-border transition-colors ' + (proofBg === 'grey' ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>Grey</button>
              <button onClick={() => setProofBg('black')} aria-pressed={proofBg === 'black'}
                className={'px-2.5 py-1 border-l border-border transition-colors ' + (proofBg === 'black' ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>Black</button>
            </div>
          </div>
          <button onClick={onDownload} disabled={bundling}
            title="Bundle every original artwork file from this order plus a print-ready proof PNG per sheet into a single .zip"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-ink text-white hover:bg-ink-2 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-wait">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {bundling ? (bundleStatus || 'Bundling…') : 'Download originals'}
          </button>
        </div>
      </div>

      {sheets.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-3 border-b border-border pb-1">
          {sheets.map((s, i) => (
            <button key={s.index} onClick={() => setActiveIdx(i)}
              className={'px-3 py-1.5 text-xs font-semibold uppercase tracking-wide rounded transition-colors ' +
                (i === activeIdx ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      {active && <DtfSheetProofCanvas sheet={active} proofBg={proofBg} orderNumber={orderNumber} />}
    </div>
  )
}

function ArtworkTile({ f, apiSrc }: { f: ProductionFile; apiSrc: (path: string | null) => string }) {
  const q = QUALITY_STYLE[f.quality]
  return (
    <div className="border border-border rounded p-3 bg-white">
      <div className="flex items-start gap-3">
        <div className="w-24 h-24 shrink-0 bg-surface border border-border rounded overflow-hidden flex items-center justify-center">
          <a href={apiSrc(f.previewUrl)} target="_blank" rel="noreferrer">
            <img src={apiSrc(f.previewUrl)} alt={f.label} className="max-w-full max-h-full object-contain" />
          </a>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="text-ink font-semibold text-sm truncate">{f.label}</div>
            {f.qty != null && f.qty > 1 && <span className="text-xs text-ink-3 shrink-0">x {f.qty}</span>}
          </div>
          {f.location && <div className="text-[10px] uppercase tracking-wider text-ink-3 mt-0.5">{f.location.replace(/_/g, ' ')}</div>}
          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-ink-3">
            <span>Print  <strong className="text-ink">{f.printWidthMm} x {f.printHeightMm} mm</strong></span>
            <span>File   <strong className="text-ink">{f.widthPx ?? '?'} x {f.heightPx ?? '?'} px</strong></span>
            <span>Min DPI <strong className="text-ink">{f.dpiMin ?? '-'}</strong></span>
            <span>Size   <strong className="text-ink">{formatBytes(f.size)}</strong></span>
          </div>
          <span className={'inline-block mt-2 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded ' + q.cls}>{q.label}</span>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <a href={apiSrc(f.downloadUrl)} download={f.originalName ?? undefined}
           className="flex-1 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide rounded bg-ink text-white hover:bg-ink-2 transition-colors text-center">
          Download for press
        </a>
        <a href={apiSrc(f.previewUrl)} target="_blank" rel="noreferrer"
           className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide rounded border border-border text-ink-2 hover:border-gold transition-colors text-center">
          Preview
        </a>
      </div>
    </div>
  )
}
